<template>
  <ServicePageLayout
    icon="fas fa-folder-open"
    title="Student Records"
    subtitle="Grievance, Violations, and Medical History Management"
  >
    <!-- Overview -->
    <section class="content-section">
      <h2>
        <i class="fas fa-info-circle section-icon" />
        Overview
      </h2>
      <p>
        The Student Records unit maintains comprehensive documentation of student affairs,
        including grievance records, disciplinary cases, and medical history. We ensure
        proper documentation, confidentiality, and due process in all student-related matters.
      </p>
    </section>

    <!-- Grievance Records -->
    <section
      id="grievance"
      class="content-section"
    >
      <h2>
        <i class="fas fa-file-signature section-icon" />
        Grievance Records
      </h2>
      <p>
        Students have the right to file grievances regarding academic or non-academic concerns.
        All grievances are handled with confidentiality and processed according to established
        procedures.
      </p>
      <div class="grievance-types">
        <div class="type-card">
          <div class="type-icon green">
            <i class="fas fa-book" />
          </div>
          <h3>Academic Grievance</h3>
          <p>Concerns related to grades, course requirements, or academic policies.</p>
        </div>
        <div class="type-card">
          <div class="type-icon yellow">
            <i class="fas fa-users" />
          </div>
          <h3>Student-to-Student</h3>
          <p>Conflicts or issues between students requiring mediation.</p>
        </div>
        <div class="type-card">
          <div class="type-icon green">
            <i class="fas fa-user-tie" />
          </div>
          <h3>Student-Faculty</h3>
          <p>Concerns involving faculty conduct or treatment of students.</p>
        </div>
        <div class="type-card">
          <div class="type-icon yellow">
            <i class="fas fa-building" />
          </div>
          <h3>Administrative</h3>
          <p>Issues with institutional services or administrative processes.</p>
        </div>
      </div>
      <button
        class="action-btn"
        @click="fileGrievance"
      >
        <i class="fas fa-edit" />
        File a Grievance
      </button>
    </section>

    <section class="content-section">
      <h2>
        <i class="fas fa-sitemap section-icon" />
        Grievance Process
      </h2>
      <div class="process-timeline">
        <div class="process-step">
          <div class="step-marker">
            1
          </div>
          <div class="step-content">
            <h4>File Complaint</h4>
            <p>Submit written complaint to OSAS with supporting documents.</p>
          </div>
        </div>
        <div class="process-step">
          <div class="step-marker">
            2
          </div>
          <div class="step-content">
            <h4>Initial Assessment</h4>
            <p>OSAS reviews complaint and determines appropriate action.</p>
          </div>
        </div>
        <div class="process-step">
          <div class="step-marker">
            3
          </div>
          <div class="step-content">
            <h4>Investigation</h4>
            <p>Fact-finding and gathering statements from involved parties.</p>
          </div>
        </div>
        <div class="process-step">
          <div class="step-marker">
            4
          </div>
          <div class="step-content">
            <h4>Mediation/Hearing</h4>
            <p>Resolution through mediation or formal hearing if necessary.</p>
          </div>
        </div>
        <div class="process-step">
          <div class="step-marker">
            5
          </div>
          <div class="step-content">
            <h4>Resolution</h4>
            <p>Decision rendered and communicated to all parties.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Violation Records -->
    <section
      id="violations"
      class="content-section highlight-warning"
    >
      <h2>
        <i class="fas fa-exclamation-triangle section-icon" />
        Violation Records
      </h2>
      <p>
        Student disciplinary records document violations of the Student Code of Conduct.
        These records are maintained to ensure campus safety and uphold institutional standards.
      </p>
      <div class="violation-categories">
        <div class="category">
          <h4>Minor Offenses</h4>
          <ul>
            <li>Improper uniform/attire</li>
            <li>Unauthorized use of facilities</li>
            <li>First instance of tardiness/cutting</li>
            <li>Littering</li>
          </ul>
        </div>
        <div class="category">
          <h4>Major Offenses</h4>
          <ul>
            <li>Cheating or plagiarism</li>
            <li>Bullying or harassment</li>
            <li>Vandalism</li>
            <li>Theft</li>
          </ul>
        </div>
        <div class="category">
          <h4>Grave Offenses</h4>
          <ul>
            <li>Physical assault</li>
            <li>Drug-related offenses</li>
            <li>Sexual misconduct</li>
            <li>Fraud/falsification</li>
          </ul>
        </div>
      </div>
    </section>

    <!-- Medical History -->
    <section
      id="medical"
      class="content-section"
    >
      <h2>
        <i class="fas fa-file-medical section-icon" />
        Medical History Records
      </h2>
      <p>
        Student medical records are maintained by the Campus Health Center in coordination
        with OSAS. These records are strictly confidential and accessed only by authorized
        personnel.
      </p>
      <div class="medical-info">
        <div class="info-card">
          <i class="fas fa-notes-medical" />
          <h4>Pre-enrollment Health Assessment</h4>
          <p>Medical examination results required for enrollment.</p>
        </div>
        <div class="info-card">
          <i class="fas fa-syringe" />
          <h4>Immunization Records</h4>
          <p>Vaccination history and compliance documentation.</p>
        </div>
        <div class="info-card">
          <i class="fas fa-clinic-medical" />
          <h4>Clinic Visit Records</h4>
          <p>Documentation of consultations and treatments at the campus clinic.</p>
        </div>
        <div class="info-card">
          <i class="fas fa-file-medical-alt" />
          <h4>Medical Certificates</h4>
          <p>Certificates issued for various purposes (PE exemption, leave, etc.).</p>
        </div>
      </div>
      <router-link
        to="/services/health-wellness"
        class="link-btn"
      >
        <i class="fas fa-clinic-medical" />
        Visit Health Services
      </router-link>
    </section>

    <section class="content-section">
      <h2>
        <i class="fas fa-download section-icon" />
        Request Records Copy
      </h2>
      <p>
        Students may request copies of their records for personal reference or other
        legitimate purposes. All requests are subject to verification and approval.
      </p>
      <div class="request-steps">
        <div class="req-step">
          <span class="req-num">1</span>
          <span>Submit request form at OSAS</span>
        </div>
        <div class="req-step">
          <span class="req-num">2</span>
          <span>Present valid ID and state purpose</span>
        </div>
        <div class="req-step">
          <span class="req-num">3</span>
          <span>Pay applicable fees (if any)</span>
        </div>
        <div class="req-step">
          <span class="req-num">4</span>
          <span>Receive records within 3-5 working days</span>
        </div>
      </div>
      <button
        class="action-btn secondary"
        @click="requestRecords"
      >
        <i class="fas fa-file-download" />
        Request Records Online
      </button>
    </section>

    <section class="content-section privacy">
      <h2>
        <i class="fas fa-shield-alt section-icon" />
        Data Privacy
      </h2>
      <p>
        In compliance with the Data Privacy Act of 2012 (RA 10173), student records are
        protected and handled with utmost confidentiality. Access to records is limited
        to authorized personnel and the student concerned.
      </p>
      <div class="privacy-points">
        <div class="privacy-point">
          <i class="fas fa-check-circle" />
          <span>Records are stored securely and accessed only by authorized staff.</span>
        </div>
        <div class="privacy-point">
          <i class="fas fa-check-circle" />
          <span>Students have the right to access and correct their personal information.</span>
        </div>
        <div class="privacy-point">
          <i class="fas fa-check-circle" />
          <span>Third-party disclosure requires written consent from the student.</span>
        </div>
        <div class="privacy-point">
          <i class="fas fa-check-circle" />
          <span>Records are retained according to institutional and legal guidelines.</span>
        </div>
      </div>
    </section>

    <section class="content-section">
      <h2>
        <i class="fas fa-phone section-icon" />
        Contact Records Unit
      </h2>
      <div class="contact-info">
        <div class="contact-item">
          <i class="fas fa-map-marker-alt" />
          <span>Room 202, Student Services Building</span>
        </div>
        <div class="contact-item">
          <i class="fas fa-phone" />
          <span>(078) 123-4567 Local 202</span>
        </div>
        <div class="contact-item">
          <i class="fas fa-envelope" />
          <span>records@nec.edu.ph</span>
        </div>
        <div class="contact-item">
          <i class="fas fa-clock" />
          <span>Monday - Friday, 8:00 AM - 5:00 PM</span>
        </div>
      </div>
    </section>
  </ServicePageLayout>
</template>

<script>
import ServicePageLayout from '@/components/landing/ServicePageLayout.vue'

export default {
  name: 'RecordsService',

  components: {
    ServicePageLayout
  },

  metaInfo: () => ({
    title: 'Student Records'
  }),

  methods: {
    fileGrievance () {
      this.$bvToast.toast('Online grievance filing coming soon!', {
        title: 'Coming Soon',
        variant: 'info',
        solid: true
      })
    },

    requestRecords () {
      this.$bvToast.toast('Online records request coming soon!', {
        title: 'Coming Soon',
        variant: 'info',
        solid: true
      })
    }
  }
}
</script>
